import React from 'react';

interface BarrelMarkProps {
  variant?: 'dark' | 'light';
  width?: number;
  height?: number;
  className?: string;
  'aria-label'?: string;
}

// Colors match the design system handoff.
//
// Menu sub-brand treatment: the mark keeps the design system's copper accent on the
// outer two hoops and carries a single teal CENTER hoop. This deliberately inverts the
// official mark, which emphasises the middle hoop with dark nodes against copper lines --
// here the emphasis is teal instead. One accent, one placement; the sub-brand distinction
// is earned by restraint rather than by recolouring the whole mark. Do not "normalize"
// the center hoop back to copper, and do not recolour the outer hoops to teal.
const DARK = {
  barrel: '#2e2010',
  stroke: '#D4854A',
  hoopOuter: '#D4854A',
  hoopCenter: '#4FA8A4',
};

const LIGHT = {
  barrel: '#F0E8DC',
  stroke: '#B5651D',
  hoopOuter: '#B5651D',
  hoopCenter: '#1F5C5A',
};

// Design system hoop grid (assets/logo-small.svg): three hoops, outer pair inset.
const HOOP_STROKE_WIDTH = 1.2;
const HOOP_NODE_RADIUS = 1.6;
const HOOP_INSET_X: [number, number] = [1.5, 20.5];
const HOOP_FULL_X: [number, number] = [0.5, 21.5];

const BarrelMark: React.FC<BarrelMarkProps> = ({
  variant = 'dark',
  width = 44,
  height = 56,
  className,
  'aria-label': ariaLabel,
}) => {
  const c = variant === 'dark' ? DARK : LIGHT;
  // SVG is drawn on a 22x28 grid, scaled via width/height
  const hoops = [
    { y: 8.5, x: HOOP_INSET_X, color: c.hoopOuter },
    { y: 14, x: HOOP_FULL_X, color: c.hoopCenter },
    { y: 19.5, x: HOOP_INSET_X, color: c.hoopOuter },
  ];

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 22 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role={ariaLabel ? 'img' : undefined}
      aria-label={ariaLabel}
    >
      {/* Tapered barrel body */}
      <path
        d="M 4,2 L 18,2 C 22,7 22,21 18,26 L 4,26 C 0,21 0,7 4,2 Z"
        fill={c.barrel}
        stroke={c.stroke}
        strokeWidth={1}
      />
      {/* Copper outer hoops (inset) framing the full-width teal center hoop */}
      {hoops.map(({ y, x: [x1, x2], color }) => (
        <React.Fragment key={y}>
          <line
            data-testid="barrel-hoop"
            x1={x1}
            y1={y}
            x2={x2}
            y2={y}
            stroke={color}
            strokeWidth={HOOP_STROKE_WIDTH}
          />
          <circle cx={x1} cy={y} r={HOOP_NODE_RADIUS} fill={color} />
          <circle cx={x2} cy={y} r={HOOP_NODE_RADIUS} fill={color} />
        </React.Fragment>
      ))}
    </svg>
  );
};

export default BarrelMark;
